import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'app/shared/apis/api.service';

export type ServiceShareAccessPolicy =
    | 'WHILE_OPEN'
    | 'AFTER_CLOSE_24H'
    | 'AFTER_CLOSE_7D'
    | 'AFTER_CLOSE_30D'
    | 'ALWAYS';

export type ServiceShareViewerContent = 'CAMERAS' | 'MAP' | 'BOTH';

export function viewerContentFromToggles(showCameras: boolean, showMap: boolean): ServiceShareViewerContent | null {
    if (showCameras && showMap) return 'BOTH';
    if (showCameras) return 'CAMERAS';
    if (showMap) return 'MAP';
    return null;
}

export function togglesFromViewerContent(viewer_content: ServiceShareViewerContent | string | null | undefined): {
    showCameras: boolean;
    showMap: boolean;
} {
    switch (viewer_content) {
        case 'CAMERAS':
            return { showCameras: true, showMap: false };
        case 'MAP':
            return { showCameras: false, showMap: true };
        default:
            return { showCameras: true, showMap: true };
    }
}

export interface ServiceShareAccessLog {
    service_share_access_log_id: number;
    accessed_at: string | Date;
    ip?: string | null;
    user_agent?: string | null;
}

export interface ServiceShareGuest {
    service_share_guest_id: number;
    email: string;
    access_policy: ServiceShareAccessPolicy;
    viewer_content: ServiceShareViewerContent;
    access_logs: ServiceShareAccessLog[];
}

export interface ServiceShareDetail {
    configured?: boolean;
    service_share_id?: number;
    service_id?: number;
    token?: string;
    active?: boolean;
    viewer_content?: ServiceShareViewerContent;
    public_path?: string;
    guests: ServiceShareGuest[];
}

export interface ServiceShareListItem {
    service_id: number;
    start: string | Date | null;
    end: string | Date | null;
    type_service: string | null;
    origin: string | null;
    destination: string | null;
    status: 'open' | 'closed';
    public_path?: string;
    token?: string;
    is_current?: boolean;
}

export interface ServiceShareBootstrap {
    mode: 'cockpit' | 'list';
    vehicle_id: number;
    service_id: number;
    viewer_content: ServiceShareViewerContent;
    service: any;
    expired_current?: boolean;
}

export const SERVICE_SHARE_TOKEN_KEY = 'prehox_service_share_token';
export const SERVICE_SHARE_LINK_TOKEN_KEY = 'prehox_service_share_link_token';

export const ACCESS_POLICY_OPTIONS: Array<{ value: ServiceShareAccessPolicy; label: string }> = [
    { value: 'WHILE_OPEN', label: 'Até o final do atendimento' },
    { value: 'AFTER_CLOSE_24H', label: '24h após o fechamento' },
    { value: 'AFTER_CLOSE_7D', label: '7 dias após o fechamento' },
    { value: 'AFTER_CLOSE_30D', label: '30 dias após o fechamento' },
    { value: 'ALWAYS', label: 'Sem expiração' },
];

@Injectable({
    providedIn: 'root',
})
export class ServiceShareService {
    constructor(private api: ApiService) {}

    getShare(serviceId: number): Observable<ServiceShareDetail> {
        return this.api.getSub(`service/${serviceId}/share`);
    }

    addGuest(
        serviceId: number,
        email: string,
        access_policy: ServiceShareAccessPolicy,
        showCameras: boolean,
        showMap: boolean,
    ): Observable<ServiceShareDetail> {
        return this.api.postSub(`service/${serviceId}/share/guests`, {
            email,
            access_policy,
            show_cameras: showCameras,
            show_map: showMap,
        });
    }

    updateGuest(
        serviceId: number,
        guestId: number,
        access_policy: ServiceShareAccessPolicy,
        showCameras: boolean,
        showMap: boolean,
    ): Observable<ServiceShareDetail> {
        return this.api.patchSub(`service/${serviceId}/share/guests/${guestId}`, {
            access_policy,
            show_cameras: showCameras,
            show_map: showMap,
        });
    }

    removeGuest(serviceId: number, guestId: number): Observable<ServiceShareDetail> {
        return this.api.deleteSub(`service/${serviceId}/share/guests/${guestId}`);
    }

    resendAccessCodes(serviceId: number): Observable<{ sent: number }> {
        return this.api.postSub(`service/${serviceId}/share/resend-codes`, {});
    }

    updateViewerContent(serviceId: number, viewer_content: ServiceShareViewerContent): Observable<ServiceShareDetail> {
        return this.api.patchSub(`service/${serviceId}/share`, { viewer_content });
    }

    getPublicMeta(token: string): Observable<{
        available: boolean;
        service_status?: 'open' | 'closed';
        message?: string;
    }> {
        return this.api.getSub(`public/service-share/${token}`);
    }

    requestOtp(token: string, email: string): Observable<{ sent: boolean }> {
        return this.api.postSub(`public/service-share/${token}/request-otp`, { email });
    }

    verifyOtp(
        token: string,
        email: string,
        code: string,
    ): Observable<{
        access_token: string;
        vehicle_id: number;
        service_id: number;
        mode: 'cockpit' | 'list';
        viewer_content: ServiceShareViewerContent;
    }> {
        return this.api.postSub(`public/service-share/${token}/verify-otp`, { email, code });
    }

    getCockpitBootstrap(): Observable<ServiceShareBootstrap> {
        return this.api.getSub('public/service-share/session/cockpit');
    }

    getSessionServices(): Observable<{ services: ServiceShareListItem[] }> {
        return this.api.getSub('public/service-share/session/services');
    }

    setSessionToken(accessToken: string, linkToken: string): void {
        sessionStorage.setItem(SERVICE_SHARE_TOKEN_KEY, accessToken);
        sessionStorage.setItem(SERVICE_SHARE_LINK_TOKEN_KEY, linkToken);
    }

    getSessionToken(): string | null {
        return sessionStorage.getItem(SERVICE_SHARE_TOKEN_KEY);
    }

    getStoredLinkToken(): string | null {
        return sessionStorage.getItem(SERVICE_SHARE_LINK_TOKEN_KEY);
    }

    /**
     * Sessão só é válida se o JWT existir e tiver sido emitido para o mesmo link público (token da URL).
     */
    hasValidSessionForLink(linkToken: string): boolean {
        const session = this.getSessionToken();
        const storedLink = this.getStoredLinkToken();
        return !!session && !!storedLink && storedLink === linkToken;
    }

    clearSessionToken(): void {
        sessionStorage.removeItem(SERVICE_SHARE_TOKEN_KEY);
        sessionStorage.removeItem(SERVICE_SHARE_LINK_TOKEN_KEY);
    }

    policyLabel(policy: ServiceShareAccessPolicy | string | null | undefined): string {
        const found = ACCESS_POLICY_OPTIONS.find((o) => o.value === policy);
        return found?.label || 'Até o final do atendimento';
    }
}
